import React, { useEffect, useMemo, useState } from 'react';
import { Pickaxe, Lock, Timer, Bomb, X, Crosshair } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { MINING_DATA } from '../../data/mining';
import { ItemIcon } from '../common/ItemIcon';
import { DynamiteModal } from './mining/DynamiteModal';
import { DronePanel } from './mining/DronePanel';


export const MiningPanel = () => {
    const skills = useStore((state) => state.skills);
    const mineAction = useStore((state) => state.mineAction);
    const miningReadyAt = useStore((state) => state.miningReadyAt);
    const resources = useStore((state) => state.resources);
    const upgrades = useStore((state) => state.upgrades);
    const drones = useStore((state) => state.drones);

    // Check if player has Dynamite License
    const hasDynamiteLicense = upgrades['mining_dynamite_license'];
    const dynamiteCount = resources['dynamite'] ?? 0;

    const throwDynamite = useStore((state) => state.throwDynamite);
    const [isTargeting, setIsTargeting] = useState(false);
    const [now, setNow] = useState(Date.now());

    // Dynamite Modal State
    const [showDynamiteModal, setShowDynamiteModal] = useState(false);
    const [selectedRockForDynamite, setSelectedRockForDynamite] = useState<any>(null); // Using any to avoid type complexity with Resource enum for now

    const handleTargetSelection = (rockId: string) => {
        if (dynamiteCount < 1) return;

        setSelectedRockForDynamite(rockId);
        setShowDynamiteModal(true);
        setIsTargeting(false);
    };

    const handleModalThrow = (score: number) => {
        if (selectedRockForDynamite) {
            return throwDynamite(score, selectedRockForDynamite as any);
        }
        return null;
    };

    const selectResourceName = (e: React.MouseEvent, rockId: string) => {
        e.stopPropagation();
        const el = document.getElementById(`resource-name-${rockId}`);
        if (el) {
            const range = document.createRange();
            range.selectNodeContents(el);
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
    };

    // Update timer for cooldowns - use real Date.now() for check to avoid stale state issues
    useEffect(() => {
        const realNow = Date.now();
        if (miningReadyAt > realNow) {
            const handle = setInterval(() => {
                const currentTime = Date.now();
                setNow(currentTime);
                if (currentTime >= miningReadyAt) {
                    clearInterval(handle);
                }
            }, 50); // Update more frequently for smoother UX and to catch 0-cooldown cases
            return () => clearInterval(handle);
        }
    }, [miningReadyAt]);

    const currentMineLevel = skills.mining?.level ?? 1;
    const currentMineXp = skills.mining?.xp ?? 0;
    const nextLevelXp = 50 * Math.pow(currentMineLevel, 2);
    const currentLevelStartXp = 50 * Math.pow(currentMineLevel - 1, 2);

    const xpNeeded = nextLevelXp - currentLevelStartXp;
    const xpProgress = currentMineXp - currentLevelStartXp;
    const progressPercent = Math.min(100, Math.max(0, (xpProgress / xpNeeded) * 100));

    // Use real Date.now() for accurate cooldown calculation to avoid stale state issues with 0-cooldown items
    const realTimeNow = Date.now();
    const remaining = Math.max(0, miningReadyAt - realTimeNow);
    const isGlobalCooldown = remaining > 0;

    // Group by Tier
    const tiers = useMemo(() => {
        const groups: Record<number, typeof MINING_DATA> = {};
        MINING_DATA.forEach(rock => {
            if (!groups[rock.tier]) {
                groups[rock.tier] = [];
            }
            groups[rock.tier]?.push(rock);
        });
        return groups;
    }, []);

    const tierNames = [
        "The Crust",
        "The Mantle",
        "The Deep Core",
        "Planetary Expansion",
        "Cosmic & Abstract"
    ];

    return (
        <Card
            title="Mining"
            headerAction={
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {hasDynamiteLicense && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                if (dynamiteCount > 0 || isTargeting) setIsTargeting(!isTargeting);
                            }}
                            className="group"
                            style={{
                                position: 'relative',
                                width: '48px',
                                height: '48px',
                                cursor: dynamiteCount > 0 ? 'pointer' : 'default',
                                opacity: dynamiteCount > 0 || isTargeting ? 1 : 0.5,
                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy transition
                                transform: isTargeting ? 'scale(1.1)' : 'scale(1)',
                                userSelect: 'none'
                            }}
                        >
                            {/* Main Circle with Gradient and Shadow */}
                            <div style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: isTargeting
                                    ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
                                    : 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
                                border: isTargeting ? '2px solid #fecaca' : '2px solid #475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: isTargeting
                                    ? '0 0 20px rgba(239, 68, 68, 0.6), inset 0 2px 4px rgba(255,255,255,0.3)'
                                    : '0 4px 6px -1px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255,255,255,0.1)',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                {/* Inner Glow/Pulse */}
                                {isTargeting && (
                                    <div className="animate-ping" style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.3)',
                                        opacity: 0.5
                                    }} />
                                )}

                                {isTargeting ? (
                                    <X size={24} color="white" strokeWidth={3} />
                                ) : (
                                    <Bomb
                                        size={24}
                                        color={dynamiteCount > 0 ? '#ef4444' : '#94a3b8'}
                                        style={{
                                            filter: dynamiteCount > 0 ? 'drop-shadow(0 2px 4px rgba(239,68,68,0.3))' : 'none',
                                            transform: 'translateY(-2px)' // Slight nudge up to make room for distinct look if needed
                                        }}
                                        className="group-hover:scale-110 transition-transform duration-200"
                                    />
                                )}
                            </div>

                            {/* Badge for Count - "Inside" the visual circle area but floating */}
                            <div style={{
                                position: 'absolute',
                                bottom: '-4px', // Overlapping bottom edge
                                right: '-4px',
                                background: isTargeting ? '#ffffff' : '#ef4444',
                                color: isTargeting ? '#ef4444' : '#ffffff',
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                minWidth: '22px',
                                height: '22px',
                                padding: '0 6px',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid var(--color-bg-panel)', // Cutout effect
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                zIndex: 10,
                                transition: 'all 0.3s ease',
                                transform: isTargeting ? 'scale(1.1)' : 'scale(1)'
                            }}>
                                {dynamiteCount}
                            </div>

                            {/* Hover Tooltip/Label */}
                            <div
                                className="group-hover:opacity-100"
                                style={{
                                    opacity: 0,
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%) translateY(8px)',
                                    background: 'rgba(0,0,0,0.8)',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    transition: 'opacity 0.2s ease',
                                    zIndex: 20
                                }}
                            >
                                {isTargeting ? 'Cancel' : 'Throw Dynamite'}
                            </div>
                        </div>
                    )}
                    <div className="level-badge" style={{ background: 'var(--color-skill-mining)' }}>Lvl {currentMineLevel}</div>
                </div>
            }

        >
            <div className="xp-container" style={{ marginBottom: '24px' }}>
                <div className="xp-bar-bg">
                    <div
                        className="xp-bar-fill"
                        style={{
                            width: `${progressPercent}%`,
                            background: 'var(--color-skill-mining)'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    <span>Level {currentMineLevel}</span>
                    <span>{currentMineXp.toLocaleString()} / {nextLevelXp.toLocaleString()} XP</span>
                    <span>Level {currentMineLevel + 1}</span>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                <div className="tiers-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {Object.entries(tiers).map(([tierStr, rocks]) => {
                        const tier = parseInt(tierStr);
                        const isTierUnlocked = currentMineLevel >= (tier === 1 ? 1 : (tier - 1) * 20); // Rough check, real check is per rock

                        if (!isTierUnlocked && tier > 1) {
                            // Optional: Hide locked tiers or show as locked
                            // For now show all but grayed out if heavily locked
                        }

                        return (
                            <div key={tier} className="tier-section">
                                <h3 style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: 'var(--color-text-primary)',
                                    borderBottom: '1px solid var(--color-border)',
                                    paddingBottom: '8px',
                                    marginBottom: '12px',
                                    opacity: 0.8
                                }}>
                                    Tier {tier}: {tierNames[tier - 1] ?? 'Unknown'}
                                </h3>
                                <div className="rocks-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                                    gap: '12px'
                                }}>
                                    {rocks.map((rock) => {
                                        const isLocked = currentMineLevel < rock.level;
                                        // Disable if locked OR global cooldown active (unless this rock has 0 cooldown? No, global cooldown blocks EVERYTHING).
                                        // Even Copper (0 cooldown) cannot be mined if I am tired from mining Iron.
                                        // But Copper sets 0 cooldown, so it doesn't block future actions.

                                        const isDisabled = isLocked || (!isTargeting && isGlobalCooldown);
                                        const isTargetable = isTargeting && !isLocked;

                                        return (
                                            <div
                                                key={rock.id}
                                                onClick={() => !isDisabled && (isTargeting ? handleTargetSelection(rock.id) : mineAction(rock.id))}
                                                className="group"
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '20px',
                                                    gap: '12px',
                                                    background: isLocked
                                                        ? 'rgba(0,0,0,0.2)'
                                                        : `linear-gradient(145deg, var(--color-bg-card-hover) 0%, rgba(255,255,255,0.03) 100%)`,
                                                    border: isTargeting
                                                        ? (isLocked ? '1px solid var(--color-border)' : '2px dashed #ef4444')
                                                        : `1px solid ${isLocked ? 'var(--color-border)' : (rock.style?.color + '40')}`, // subtle border
                                                    borderRadius: '16px',
                                                    opacity: isLocked ? 0.5 : 1,
                                                    minHeight: '140px',
                                                    position: 'relative',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    overflow: 'hidden',
                                                    cursor: isDisabled ? 'not-allowed' : (isTargeting ? 'crosshair' : 'pointer'),
                                                    boxShadow: isTargeting && !isLocked
                                                        ? '0 0 20px rgba(239, 68, 68, 0.2)'
                                                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                    transform: isTargetable ? 'scale(1.02)' : 'scale(1)',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                {/* Hover Glow Effect */}
                                                {!isLocked && !isDisabled && (
                                                    <div
                                                        className="group-hover:opacity-100"
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background: `radial-gradient(circle at center, ${rock.style?.color}15 0%, transparent 70%)`,
                                                            opacity: 0,
                                                            transition: 'opacity 0.3s ease',
                                                            pointerEvents: 'none'
                                                        }}
                                                    />
                                                )}

                                                {/* Cooldown Overlay */}
                                                {isGlobalCooldown && !isLocked && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(15, 23, 42, 0.6)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 20,
                                                        color: 'var(--color-accent)',
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        backdropFilter: 'blur(2px)',
                                                        borderRadius: '16px',
                                                        transition: 'all 0.2s ease'
                                                    }}>
                                                        <Timer className="animate-spin-slow" size={20} style={{ marginBottom: '4px' }} />
                                                        {(remaining / 1000).toFixed(1)}s
                                                    </div>
                                                )}

                                                {isLocked ? (
                                                    <div style={{
                                                        width: '48px', height: '48px',
                                                        borderRadius: '50%', background: 'rgba(0,0,0,0.2)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }}>
                                                        <Lock size={20} color="var(--color-text-muted)" />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="transform transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1"
                                                        onDoubleClick={(e) => selectResourceName(e, rock.id)}
                                                    >
                                                        <ItemIcon id={rock.id} size={42} />
                                                    </div>
                                                )}

                                                <div style={{ textAlign: 'center', zIndex: 10 }}>
                                                    <h4
                                                        id={`resource-name-${rock.id}`}
                                                        style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            margin: '0 0 4px 0',
                                                            color: isLocked ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                                                            letterSpacing: '0.01em',
                                                            userSelect: 'text',
                                                            cursor: 'text'
                                                        }}
                                                    >
                                                        {rock.name}
                                                    </h4>

                                                    {!isLocked && (
                                                        <p style={{
                                                            fontSize: '0.75rem',
                                                            color: 'var(--color-text-secondary)',
                                                            margin: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px'
                                                        }}>
                                                            <span style={{ background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px' }}>
                                                                {rock.baseXp} XP
                                                            </span>
                                                            <span style={{ opacity: 0.5 }}>•</span>
                                                            <span style={{ opacity: 0.8 }}>
                                                                {(rock.baseCooldown / 1000)}s
                                                            </span>
                                                        </p>
                                                    )}
                                                    {isLocked && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Lvl {rock.level}</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                </div>

                {drones.activeDrones.length > 0 && (
                    <div style={{
                        width: '320px',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '24px',
                        alignSelf: 'flex-start',
                        height: 'calc(100vh - 200px)', // Rough estimate to keep it within view
                        overflowY: 'auto'
                    }}>
                        <DronePanel vertical={true} />
                    </div>
                )}
            </div>

            <DynamiteModal
                isOpen={showDynamiteModal}
                onClose={() => {
                    setShowDynamiteModal(false);
                    setSelectedRockForDynamite(null);
                }}
                onThrow={handleModalThrow}
                rockId={selectedRockForDynamite}
            />
        </Card >
    );
};
